import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { useDropdownFit } from 'hooks/useDropdownFit';
import { getAnchorRect } from './placeholders/caretAnchor';

// Inner edges of the nearest horizontally-clipping ancestor (or the document). clientWidth excludes
// the scrollbar, so clamping the dropdown to this can't itself force a horizontal scrollbar.
function horizontalClipBounds(el: HTMLElement | null): { left: number; right: number } {
  for (let node = el?.parentElement ?? null; node; node = node.parentElement) {
    if (/(auto|scroll|hidden|clip)/.test(getComputedStyle(node).overflowX)) {
      const left = node.getBoundingClientRect().left + node.clientLeft;
      return { left, right: left + node.clientWidth };
    }
  }
  return { left: 0, right: document.documentElement.clientWidth };
}

interface DropdownPlacementParams {
  editor: Editor;
  container: HTMLElement | null;
  resolutionOpen: boolean;
  resolutionAnchorPos: number | null;
  autocompleteOpen: boolean;
  getAutocompleteAnchorRect: () => DOMRect | null;
  autocompleteItemCount: number;
}

export function useDropdownPlacement({
  editor,
  container,
  resolutionOpen,
  resolutionAnchorPos,
  autocompleteOpen,
  getAutocompleteAnchorRect,
  autocompleteItemCount,
}: DropdownPlacementParams) {
  const getAnchorBottom = useCallback(() => {
    if (resolutionOpen && resolutionAnchorPos !== null) {
      try {
        return editor.view.coordsAtPos(resolutionAnchorPos).bottom;
      } catch {
        return null;
      }
    }
    if (autocompleteOpen) return getAutocompleteAnchorRect()?.bottom ?? null;
    return null;
  }, [editor, resolutionOpen, resolutionAnchorPos, autocompleteOpen, getAutocompleteAnchorRect]);
  const getScrollTarget = useCallback(() => container, [container]);
  const anchorKey = resolutionOpen
    ? `list:${resolutionAnchorPos}`
    : autocompleteOpen
      ? 'autocomplete'
      : null;
  const maxHeight = useDropdownFit(
    getAnchorBottom,
    resolutionOpen || autocompleteOpen,
    getScrollTarget,
    anchorKey
  );

  const containerRect = container?.getBoundingClientRect() ?? null;
  const autocompleteRect = autocompleteOpen ? getAutocompleteAnchorRect() : null;
  const autocompleteAnchor =
    autocompleteRect && containerRect
      ? {
          top: autocompleteRect.bottom - containerRect.top,
          left: autocompleteRect.left - containerRect.left,
        }
      : null;
  const resolutionAnchor =
    resolutionOpen && resolutionAnchorPos !== null
      ? getAnchorRect(editor, resolutionAnchorPos, container)
      : null;

  // Slide the caret-pinned dropdown left when it would overflow the right clip edge. Retrigger only
  // on layout-independent signals (caret document position, item count) and read the caret through a
  // ref, so the shift can't perturb its own inputs and loop.
  const caretViewportLeft =
    autocompleteRect?.left ??
    (resolutionAnchor && containerRect ? containerRect.left + resolutionAnchor.left : null);
  const caretViewportLeftRef = useRef(caretViewportLeft);
  caretViewportLeftRef.current = caretViewportLeft;
  const cursorPos = resolutionOpen || autocompleteOpen ? editor.state.selection.from : -1;
  const [leftShift, setLeftShift] = useState(0);
  useLayoutEffect(() => {
    const caretLeft = caretViewportLeftRef.current;
    const dropdown = container?.querySelector<HTMLElement>('[role="listbox"]');
    if (caretLeft === null || !dropdown) {
      setLeftShift(0);
      return;
    }
    const margin = 8;
    const { left: clipLeft, right: clipRight } = horizontalClipBounds(container);
    const maxLeft = clipRight - margin - dropdown.offsetWidth;
    const target = Math.max(clipLeft + margin, Math.min(caretLeft, maxLeft));
    setLeftShift(target - caretLeft);
  }, [container, anchorKey, cursorPos, autocompleteItemCount]);

  return {
    maxHeight,
    autocompleteAnchor: autocompleteAnchor
      ? { ...autocompleteAnchor, left: autocompleteAnchor.left + leftShift }
      : undefined,
    resolutionAnchor: resolutionAnchor
      ? { ...resolutionAnchor, left: resolutionAnchor.left + leftShift }
      : undefined,
  };
}
